import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline } from "@mdi/js";
import {
  Button,
  Drawer,
  Input,
  NumberField,
  Spinner,
  Switch,
  TextField,
} from "@heroui/react";
import { gasStationFormStore } from "@/stores/GasStationFormStore";
import LeafletMap from "@/features/constractors/drawers/ServiceCenterDrawer/LeafletMap";
import { STATUS_OPTIONS } from "../constants";
import "@/features/constractors/drawers/ServiceCenterDrawer/ServiceCenterDrawer.css";

interface GasStationDrawerProps {
  onSuccess?: () => void;
}

const GasStationDrawer: React.FC<GasStationDrawerProps> = observer(
  ({ onSuccess }) => {
    const store = gasStationFormStore;

    const handleClose = () => store.close();
    const handleSave = () => store.submit(onSuccess);

    const getFieldError = (field: string): string | undefined =>
      store.submitAttempted ? store.validationErrors[field] : undefined;

    return (
      <Drawer.Backdrop
        isOpen={store.isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        className="sc-drawer-backdrop"
      >
        <Drawer.Content placement="right" className="sc-drawer-content">
          <Drawer.Dialog className="sc-drawer-dialog">
            <Drawer.CloseTrigger />
            <Drawer.Header className="sc-drawer-header">
              <Drawer.Heading>
                {store.isEditing ? "Редактирование заправки" : "Новая заправка"}
              </Drawer.Heading>
              <p className="sc-drawer-subtitle">
                {store.isEditing
                  ? "Измените данные и доступность топлива"
                  : "Заполните данные для добавления заправки"}
              </p>
            </Drawer.Header>

            <Drawer.Body className="sc-drawer-body">
              {store.isLoading ? (
                <div className="gs-drawer__loading">
                  <Spinner size="lg" />
                </div>
              ) : (
                <>
                  <div className="sc-drawer-section">
                    <span className="sc-drawer-section__title">
                      Основная информация
                    </span>
                    <Switch
                      isSelected={store.isActive}
                      onChange={(value) => store.setIsActive(value)}
                    >
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Content className="sc-drawer-published-label">
                        {store.isActive ? "Активна" : "Неактивна"}
                      </Switch.Content>
                    </Switch>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">
                      Название <span className="sc-drawer-field__req">*</span>
                    </label>
                    <TextField
                      fullWidth
                      isInvalid={Boolean(getFieldError("name"))}
                    >
                      <Input
                        value={store.name}
                        onChange={(e) => store.setName(e.target.value)}
                        placeholder="Название заправки"
                      />
                    </TextField>
                    {getFieldError("name") && (
                      <p className="sc-drawer-field__error">
                        {getFieldError("name")}
                      </p>
                    )}
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">Бренд</label>
                    <TextField fullWidth>
                      <Input
                        value={store.brand}
                        onChange={(e) => store.setBrand(e.target.value)}
                        placeholder="Лукойл, Газпромнефть..."
                      />
                    </TextField>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">Адрес</label>
                    <TextField fullWidth>
                      <Input
                        value={store.address}
                        onChange={(e) => store.setAddress(e.target.value)}
                        placeholder="Адрес заправки"
                      />
                    </TextField>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">OSM ID</label>
                    <TextField fullWidth>
                      <Input
                        value={store.osmId}
                        onChange={(e) => store.setOsmId(e.target.value)}
                        placeholder="Внешний идентификатор"
                      />
                    </TextField>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">Статус</label>
                    <div className="gs-drawer__status-group">
                      {STATUS_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          size="sm"
                          variant={
                            store.status === option.value ? "primary" : "tertiary"
                          }
                          onPress={() => store.setStatus(option.value)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">
                      Дополнительно
                    </label>
                    <TextField fullWidth>
                      <Input
                        value={store.detail}
                        onChange={(e) => store.setDetail(e.target.value)}
                        placeholder="Комментарий для пользователей"
                      />
                    </TextField>
                  </div>

                  <div className="sc-drawer-section">
                    <span className="sc-drawer-section__title">
                      Доступность топлива
                    </span>
                  </div>
                  <p className="sc-drawer-field__hint">
                    Отключите вид топлива или оставьте цену пустой, если его нет
                    в наличии
                  </p>

                  <div className="gs-drawer__fuels">
                    {store.fuels.map((fuel) => (
                      <div key={fuel.key} className="gs-drawer__fuel-row">
                        <div className="gs-drawer__fuel-label">
                          <Switch
                            isSelected={fuel.available}
                            onChange={(value) =>
                              store.setFuelAvailable(fuel.key, value)
                            }
                          >
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                            <Switch.Content>{fuel.label}</Switch.Content>
                          </Switch>
                        </div>
                        <NumberField
                          fullWidth
                          isDisabled={!fuel.available}
                          value={
                            fuel.price
                              ? Number(fuel.price.replace(",", "."))
                              : undefined
                          }
                          onChange={(value) =>
                            store.setFuelPrice(
                              fuel.key,
                              value == null ? "" : String(value),
                            )
                          }
                          minValue={0}
                          step={0.01}
                          className="gs-drawer__fuel-price"
                        >
                          <NumberField.Group className="gs-drawer__fuel-price-group">
                            <NumberField.Input
                              className="gs-drawer__fuel-price-input"
                              placeholder="Цена, ₽"
                            />
                          </NumberField.Group>
                        </NumberField>
                        {store.submitAttempted && store.fuelErrors[fuel.key] && (
                          <p className="sc-drawer-field__error gs-drawer__fuel-error">
                            {store.fuelErrors[fuel.key]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {store.isEditing && (
                    <div className="gs-drawer__override">
                      <Icon path={mdiAlertCircleOutline} size={0.75} />
                      <Switch
                        isSelected={store.clearAdminOverride}
                        onChange={(value) => store.setClearAdminOverride(value)}
                      >
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        <Switch.Content>
                          Сбросить админ-оверрайд и пересчитать из отчётов
                        </Switch.Content>
                      </Switch>
                    </div>
                  )}

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">Координаты</label>
                    <div className="sc-drawer-coords">
                      <TextField fullWidth>
                        <Input
                          readOnly
                          value={store.lat.toFixed(6)}
                          aria-label="Широта"
                        />
                      </TextField>
                      <TextField fullWidth>
                        <Input
                          readOnly
                          value={store.lon.toFixed(6)}
                          aria-label="Долгота"
                        />
                      </TextField>
                    </div>
                  </div>

                  <div className="sc-drawer-field">
                    <label className="sc-drawer-field__label">
                      Расположение на карте
                    </label>
                    <p className="sc-drawer-field__hint">
                      Перетащите маркер для уточнения местоположения
                    </p>
                    {store.isOpen && (
                      <LeafletMap
                        lat={store.lat}
                        lon={store.lon}
                        onPositionChange={(lat, lon) =>
                          store.setMapPosition(lat, lon)
                        }
                      />
                    )}
                  </div>
                </>
              )}
            </Drawer.Body>

            <Drawer.Footer className="sc-drawer-footer">
              <Button variant="ghost" onPress={handleClose}>
                Отмена
              </Button>
              <Button
                variant="primary"
                onPress={handleSave}
                isPending={store.isSubmitting}
                isDisabled={store.isLoading}
              >
                {store.isEditing ? "Сохранить" : "Создать"}
              </Button>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    );
  },
);

GasStationDrawer.displayName = "GasStationDrawer";
export default GasStationDrawer;
